import { defineStore } from 'pinia'
import { ref } from 'vue'
import { checkGlobalAdminStatus, claimFirstGlobalAdmin, userAzureId, isPopupBlocked } from "@/helpers/auth";
import axiosClient from '@/api/axiosClient';
// FJERNET: useMenuStore - brukes ikke lenger her
import { useNotificationsStore } from "@/store/notificationsStore";

export const useIcs4Store = defineStore('ics4Store', () =>
{

  const notificationsStore = useNotificationsStore();


  let fetching = false;
  const isAuth = ref(false);
  const userName = ref('');
  const isGlobalAdmin = ref(false);
  const globalAdminSetupSessionId = ref(null); // For first-time setup
  const curUserDbsAndRoles = ref(null);  // All databases with roles for user
  const loadingError = ref(null); // For visning av feilmelding i UI
  const isLoading = ref(false); // For visning av loading state i UI

  // Refresh GlobalAdmin status from the dedicated endpoint. Non-critical: a
  // failure is swallowed and the user continues without GlobalAdmin privileges.
  async function refreshGlobalAdminStatus ()
  {
    try {
      const gaStatus = await checkGlobalAdminStatus();
      isGlobalAdmin.value = gaStatus.isGlobalAdmin;
      globalAdminSetupSessionId.value = gaStatus.setupSessionId || null;
    } catch (gaError) {
      console.warn('[Ics4] Kunne ikke sjekke GlobalAdmin status:', gaError);
      isGlobalAdmin.value = false;
      globalAdminSetupSessionId.value = null;
    }
  }

  // Load the current user's databases and roles from GET /api/user/databases,
  // translating each error class into UI state (popup blocked / 403 / 404 / 401 / other).
  async function loadUserDatabases ()
  {
    try {
      const res = await axiosClient.get('user/databases');
      curUserDbsAndRoles.value = res.data;
      console.log('[Ics4] curUserDbsAndRoles lastet:', curUserDbsAndRoles.value);
    } catch (error) {
      // Token could not be acquired because a popup was blocked.
      if (isPopupBlocked.value || error.message?.includes('popup blokkert')) {
        console.warn('[Ics4] Kunne ikke laste databaser - popup er blokkert');
        loadingError.value = 'Popup er blokkert. Bruk alternativ innlogging eller tillat popup.';
        curUserDbsAndRoles.value = [];
        return;
      }

      // 403 Forbidden: authenticated but not authorized (not in Rbac.UserAccount).
      // Expected for new users, who may then submit an access request.
      if (error.response?.status === 403) {
        console.log('[Ics4] Bruker ikke autorisert (403 Forbidden) - viser tilgangsforespørsel');
        curUserDbsAndRoles.value = [];
      }
      // 404 not found on the new user/databases endpoint.
      else if (error.response?.status === 404) {
        console.log('[Ics4] Endepunkt ikke funnet (404)');
        curUserDbsAndRoles.value = [];
      }
      // 401 Unauthorized: token invalid or expired.
      else if (error.response?.status === 401) {
        console.warn('[Ics4] Token ugyldig eller utløpt (401 Unauthorized)');
        loadingError.value = 'Sesjonen har utløpt. Vennligst logg inn på nytt.';
        curUserDbsAndRoles.value = [];
      }
      else {
        // Any other error: log it and show the user what went wrong.
        console.error('[Ics4] Feil ved lasting av databaser:', error);
        loadingError.value = 'Det oppstod en feil ved lasting av prosjekter. Prøv igjen.';
        curUserDbsAndRoles.value = [];
      }
    }
  }

  async function loadDbsAndRole ()
  {
    console.log('loadDbsAndRole()', curUserDbsAndRoles.value != null || fetching);
    // Hindre at denne lastes fra basen flere ganger
    if (curUserDbsAndRoles.value != null || fetching) {
      return;
    }

    fetching = true;
    isLoading.value = true;
    loadingError.value = null;
    await notificationsStore.addInfo("Laster oppsett");

    try {
      if (userAzureId.value !== "") {
        await refreshGlobalAdminStatus();
        await loadUserDatabases();
      } else {
        curUserDbsAndRoles.value = null;
      }
    } finally {
      fetching = false;
      isLoading.value = false;
    }
  }

  // Claim første GlobalAdmin ved first-time setup
  async function claimFirstGlobalAdminRole () {
    if (!globalAdminSetupSessionId.value) {
      console.error('[Ics4] Ingen setupSessionId tilgjengelig');
      return false;
    }

    const success = await claimFirstGlobalAdmin(globalAdminSetupSessionId.value);
    if (success) {
      isGlobalAdmin.value = true;
      globalAdminSetupSessionId.value = null;
      await notificationsStore.addSuccess('GlobalAdmin', 'Du er nå første GlobalAdmin!');
    } else {
      await notificationsStore.addError('Feil', 'Kunne ikke sette opp GlobalAdmin. Prøv igjen.');
    }
    return success;
  }

  // Nullstill all session-scopet tilstand ved identitetsendring (logout / konto-bytte, WI 2255).
  // Nuller også `fetching`-vakten så neste bruker faktisk laster databaser på nytt.
  function reset () {
    fetching = false;
    isAuth.value = false;
    userName.value = '';
    isGlobalAdmin.value = false;
    globalAdminSetupSessionId.value = null;
    curUserDbsAndRoles.value = null;
    loadingError.value = null;
    isLoading.value = false;
  }

  return {
    isAuth,
    loadDbsAndRole, curUserDbsAndRoles,
    userName, isGlobalAdmin,
    globalAdminSetupSessionId, claimFirstGlobalAdminRole,
    // Nye: loading state og feilhåndtering
    isLoading, loadingError,
    reset
  }
})
